/**
 * FOR SALE — « Ventes en cours » de l'équipe (/pages/ventes, section fs-team-sales ; refonte de l'espace équipe, lot 1).
 *
 * La page Liquid ne porte aucune donnée : ce module attend la session (window.FSAccount, fs-account.js), lit /me, puis,
 * pour un compte de l'équipe seulement, GET /admin/sales (rôle relu par le service à chaque appel). Hors équipe : « page
 * réservée à l'équipe », aucun appel de plus.
 * Une vente = la vraie carte de l'annonce, demandée à la boutique par la Section Rendering API sur la page de l'annonce
 * (…/pages/listings/<handle>?section_id=fs-team-sale-card : HTML public, en cache, celui du catalogue), et son dossier
 * composé sur les lignes de la carte : zone haute à la hauteur de la baie (état, phrase, piste Payé → Clôturé dans la
 * couleur du jeu), zone du milieu (tickets acheteur et vendeur, « Discord ↗ »), pied à la hauteur du pied de la carte
 * (geste attendu, net vendeur sur la ligne du prix). Carte indisponible (annonce retirée, brouillon) : l'identité de
 * l'annonce lue sur le service, sans fausse carte.
 * Une étape sans fait enregistré est « inconnue », jamais devinée.
 * Gestes du pied (lot 2) : un geste principal, au plus un secondaire, ceux que le service permet à ce compte
 * (`sale.actions`) : POST /admin/sales/<dossier>/<geste>, qui reprend les chemins existants (checklist, « Sale complete »,
 * registre vendeur, /fermer). La commande Discord équivalente reste dite à côté : chacun garde son outil. Confirmation
 * dans le pied (jamais confirm()), sauf pour cocher une étape (un clic, sans saisie). La page renvoie les jetons qu'elle a
 * montrés (`sale.tokens`, séquence et reste dû du registre) et un requestId gardé tant que la réponse est incertaine.
 * Après un geste : la vente relue remplace la sienne, toast sobre, et si elle sort de la file, le tampon de la carte est
 * refrappé puis le dossier se replie (rien en mouvement réduit). Relance : aucune réponse type n'existe encore pour
 * « confirmez la réception » ni « acceptez le changement d'e-mail » : lien vers le bon ticket, « Relancer … ↗ ».
 * Relu au retour sur l'onglet (30 s au moins entre deux lectures) ; aucune boucle de sondage. Textes en français (espace
 * équipe, décision INTL). Fonctions pures exportées pour les tests Node.
 */
import { el as h, put, add, alert, toast, field, setFieldError } from './fs-ui.js';
import { path } from './fs-i18n.js';

export const CARD_SECTION = 'fs-team-sale-card';
export const REFRESH_MS = 30_000;
export const STEP_NAMES = Object.freeze({ paid: 'Payé', access: 'Accès remis', email: 'E-mail changé', confirmed: 'Confirmé', seller_paid: 'Vendeur payé', closed: 'Clôturé' });
const STATE_WORDS = Object.freeze({ done: 'fait', current: 'en cours', todo: 'à venir', unknown: 'inconnu', na: 'sans objet' });
export const STAGE_NAMES = Object.freeze({ support: 'Support', delivery: 'Delivery Center', review: 'Selling Reviews', listed: 'Listed Accounts', payout: 'Payout Center', closed: 'Closed Tickets' });
const GAME_NAMES = Object.freeze({ waruniverse: 'WarUniverse', redgalaxy: 'Red Galaxy', darkorbit: 'DarkOrbit', novahorizon: 'Nova Horizon' });
const DELIVERY_STEPS = Object.freeze({ seller_access_received: 'accès reçus du vendeur', access_checked: 'connexion vérifiée', email_changed: 'e-mail changé',
  credentials_sent: 'accès remis', password_changed: 'mot de passe changé', buyer_confirmed: 'l’acheteur a confirmé' });
const FEED_TEXT = Object.freeze({ paid: 'paiement reçu', link_sent: 'lien de paiement envoyé', concluded: 'vente conclue', buyer_ticket_closed: 'ticket acheteur fermé',
  buyer_ticket_deleted: 'ticket acheteur supprimé', seller_due: 'règlement du vendeur constaté', seller_paid: 'vendeur payé' });
const HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;
const DISCORD_CHANNEL = /^https:\/\/discord\.com\/channels\/\d{17,20}\/\d{17,20}$/;
const H = 3_600_000, DAY = 86_400_000;
const time = (value) => { const at = Date.parse(value ?? ''); return Number.isFinite(at) ? at : null; };
const text = (value, max = 160) => (typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max) : '');

/** Heure seule le jour même, sinon jour et heure (« 27 oct. 18:40 »), à l'heure du navigateur. */
export function clock(iso, now = Date.now()) {
  const at = time(iso);
  if (at === null) return '';
  const date = new Date(at), today = new Date(now).toDateString() === date.toDateString();
  const hour = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return today ? hour : `${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} ${hour}`;
}
/** Durée écoulée courte : « 40 min », « 3 h », « 2 j » (espace insécable) ; null sans date. */
export function span(iso, now = Date.now()) {
  const at = time(iso);
  if (at === null) return null;
  const elapsed = Math.max(0, now - at);
  // Espace insécable : le nombre ne se sépare jamais de son unité en fin de ligne (« 20 / h. » sur téléphone).
  if (elapsed < H) return `${Math.max(1, Math.round(elapsed / 60_000))}\u00a0min`;
  if (elapsed < 2 * DAY) return `${Math.round(elapsed / H)}\u00a0h`;
  return `${Math.round(elapsed / DAY)}\u00a0j`;
}
const ago = (iso, now) => (span(iso, now) ? `il y a ${span(iso, now)}` : '');
/** Montant en entiers mineurs, formaté (aucun calcul) : « 94 € », « 94,50 € ». */
export function money(minor, currency = { code: 'EUR', minorUnit: 2 }) {
  if (!Number.isSafeInteger(minor)) return '';
  const unit = 10 ** (currency?.minorUnit ?? 2), whole = minor % unit === 0;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency?.code || 'EUR', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(minor / unit);
}

/** Texte de l'étape sous son nom ; `current` : c'est le geste attendu. */
export function stepTime(step, { current = false, now = Date.now() } = {}) {
  if (step.state === 'na') return 'sans objet';
  if (step.state === 'done') return step.at ? clock(step.at, now) : step.source === 'conclusion' ? 'à la conclusion' : 'date inconnue';
  if (step.state === 'unknown') return step.code === 'seller_paid' ? 'non suivi ici' : current ? 'à vérifier' : 'inconnu';
  if (step.code === 'paid' && step.at) return `lien ${clock(step.at, now)}`;
  if (step.owed && step.at) return `dû depuis ${span(step.at, now)}`;
  if (step.hint?.code === 'buyer_confirmed') return `OK ${clock(step.hint.at, now)}`;
  if (step.code === 'closed' && step.partial) return 'un sur deux';
  return current ? 'à faire' : '—';
}

const WHO = Object.freeze({ buyer: 'l’acheteur', seller: 'le vendeur', admin: 'un administrateur' });
const ACT = Object.freeze({ deliver: 'remettre et conclure', conclude: 'conclure', mark_due: 'constater le dû', pay_seller: 'payer le vendeur', settle: 'payer le vendeur',
  close_tickets: 'clôturer' });
/** Pastille d'état : « À toi · conclure », « On attend l'acheteur · 3 h ». */
export function whoText(sale, now = Date.now()) {
  if (sale.group === 'mine') return { text: `À toi · ${ACT[sale.gesture] ?? 'agir'}`, tone: 'mine' };
  const wait = span(sale.since, now);
  return { text: `On attend ${WHO[sale.waitingOn] ?? 'quelqu’un'}${wait ? ` · ${wait}` : ''}`, tone: 'wait' };
}

/** Phrase de la vente : un fait, puis ce qui reste (atténué). */
export function sentence(sale, now = Date.now()) {
  const step = (code) => sale.steps.find((row) => row.code === code) ?? {};
  const paid = step('paid').at, concluded = step('confirmed'), seller = sale.tickets?.seller, buyer = sale.tickets?.buyer;
  const shut = (place) => ['closed', 'deleted'].includes(place?.location);
  switch (sale.gesture) {
    case 'await_payment': return { lead: `Lien de paiement envoyé ${ago(sale.since, now)}.`, rest: 'Au paiement, la vente passe dans « À toi ».' };
    case 'await_order': return { lead: 'Commande passée.', rest: 'Le paiement n’est pas encore confirmé par Shopify.' };
    case 'deliver': return { lead: paid ? `Payée ${ago(paid, now)}.` : 'Payée.', rest: 'Les accès se remettent dans le ticket acheteur ; notez-le ici d’un clic.' };
    case 'conclude': return { lead: step('confirmed').hint ? `L’acheteur a confirmé ${ago(step('confirmed').hint.at, now)}.` : 'Livraison cochée.', rest: 'Il reste à conclure la vente.' };
    case 'await_buyer': return { lead: `Accès remis ${ago(sale.since, now)}.`, rest: 'L’acheteur doit confirmer que tout fonctionne.' };
    case 'await_seller_email': return { lead: `Accès remis ${ago(sale.since, now)}.`, rest: 'Le vendeur doit accepter le changement d’e-mail.' };
    case 'mark_due': return { lead: concluded.at ? `Vente conclue ${ago(concluded.at, now)}.` : 'Vente conclue.', rest: 'Le règlement du vendeur n’est pas encore constaté.' };
    case 'pay_seller': return { lead: 'Vente conclue.', rest: `Le vendeur attend son paiement depuis ${span(sale.since, now) ?? 'la conclusion'}.` };
    case 'settle': return { lead: concluded.at ? `Vente conclue ${ago(concluded.at, now)}.` : 'Vente conclue.', rest: 'Le paiement du vendeur n’est pas suivi sur le site pour cette annonce.' };
    case 'await_admin': return sale.pending === 'conclude'
      ? { lead: step('confirmed').hint ? `L’acheteur a confirmé ${ago(step('confirmed').hint.at, now)}.` : 'Livraison cochée.', rest: 'Un administrateur doit conclure la vente.' }
      : { lead: concluded.at ? `Vente conclue ${ago(concluded.at, now)}.` : 'Vente conclue.', rest: 'Un administrateur doit payer le vendeur.' };
    case 'close_tickets': {
      const left = shut(buyer) ? 'le ticket vendeur' : shut(seller) || !seller ? 'le ticket acheteur' : 'les deux tickets';
      return { lead: step('seller_paid').at ? `Vendeur payé ${ago(step('seller_paid').at, now)}.` : 'Vendeur payé.', rest: `Il reste à fermer ${left}.` };
    }
    default: return { lead: 'Vente en cours.', rest: '' };
  }
}

/** Libellés des gestes du pied (codes de POST /admin/sales/<dossier>/<geste>). */
export const ACTION_LABELS = Object.freeze({ access: 'Accès remis', email: 'E-mail changé', conclude: 'Conclure', mark_due: 'Constater le dû', payout: 'Noter le versement',
  close: 'Clôturer' });

/**
 * Pied du dossier : le geste attendu en mots, la commande Discord qui le fait déjà (`hint`, `code`), le geste principal
 * et au plus un secondaire, parmi ceux que le service permet (`sale.actions`) ; sinon un lien : relancer dans le bon
 * ticket, ou l'outil actuel de l'équipe (dossier vendeur de l'espace équipe) quand la page ne peut pas faire le geste.
 */
export function gestureOf(sale) {
  const handle = HANDLE.test(sale.listing?.handle ?? '') ? sale.listing.handle : '';
  const workspace = { href: handle ? `/pages/moderation?handle=${encodeURIComponent(handle)}` : '/pages/moderation', label: 'Espace équipe' };
  const net = sale.money?.outstandingMinor || sale.money?.dueMinor || null;
  const can = (code) => (Array.isArray(sale.actions) && sale.actions.includes(code) ? code : null);
  const ticket = (kind) => (DISCORD_CHANNEL.test(sale.tickets?.[kind]?.url ?? '') ? sale.tickets[kind].url : null);
  const nudge = (kind) => (ticket(kind) ? { href: ticket(kind), label: kind === 'buyer' ? 'Relancer l’acheteur' : 'Relancer le vendeur', external: true,
    aria: `Relancer ${kind === 'buyer' ? 'l’acheteur' : 'le vendeur'} de ${sale.listing?.reference ?? ''} dans Discord (nouvel onglet)` } : null);
  const pick = (base, ...options) => {
    const [primary = null, secondary = null] = options.filter(Boolean);
    return { ...base, primary, secondary };
  };
  switch (sale.gesture) {
    case 'await_payment': return pick({ text: 'Rien à faire', hint: 'au paiement, le ticket passe seul dans Delivery Center' });
    case 'await_order': return pick({ text: 'Rien à faire', hint: 'Shopify confirme le paiement' });
    case 'deliver': return pick(can('conclude') ? { text: 'Remettre les accès dans le ticket acheteur', hint: 'puis conclure, ici ou dans Discord :', code: 'Sale complete' }
      : { text: 'Remettre les accès dans le ticket acheteur', hint: 'puis le noter ici ; un administrateur conclut' }, can('access'), can('conclude'));
    case 'conclude': return pick({ text: 'Conclure la vente', hint: 'ici, ou dans le ticket acheteur :', code: 'Sale complete' }, can('conclude'));
    case 'await_buyer': return pick({ text: 'L’acheteur doit confirmer la réception', hint: 'relance dans son ticket au besoin' }, nudge('buyer'), can('conclude'));
    case 'await_seller_email': return pick({ text: 'Changement d’e-mail en attente', hint: 'le vendeur l’accepte ; notez-le ici une fois fait' }, can('email'), nudge('seller'));
    case 'mark_due': return pick({ text: 'Constater le règlement dû', hint: can('mark_due') ? 'après la livraison validée, dans le registre vendeur' : 'dans le dossier du vendeur' }, can('mark_due') ?? { ...workspace });
    // Le montant est déjà sur la ligne du prix (figureOf) ; sans montant (MiddleMan), celui du registre n'est jamais redit.
    case 'pay_seller': return pick({ text: !sale.money && net ? `Payer le vendeur · ${money(net)}` : 'Payer le vendeur', hint: can('payout') ? 'puis noter le versement ici' : 'puis l’enregistrer dans le dossier du vendeur' },
      can('payout') ?? { ...workspace });
    case 'settle': return pick({ text: 'Payer le vendeur, puis fermer les tickets', hint: 'paiement non suivi ici ; fermeture ici ou dans chaque ticket :', code: '/fermer' }, can('close'), { ...workspace, label: 'Dossier vendeur' });
    case 'close_tickets': return pick({ text: 'Fermer les tickets', hint: 'ici, ou dans chaque ticket :', code: '/fermer' }, can('close'));
    case 'await_admin': return pick({ text: 'Rien à faire', hint: sale.pending === 'conclude' ? 'un administrateur conclut la vente' : 'un administrateur paie le vendeur' });
    default: return pick({ text: '' });
  }
}

const STEP_WORDS = Object.freeze({ seller_access_received: 'accès reçus du vendeur', access_checked: 'connexion vérifiée', email_changed: 'e-mail changé', credentials_sent: 'accès remis',
  password_changed: 'mot de passe changé', buyer_confirmed: 'confirmation de l’acheteur' });
/** Référence de preuve admise par le registre vendeur (même motif que l'espace équipe). */
export const EVIDENCE = /^[a-zA-Z0-9][a-zA-Z0-9_.:/-]{0,119}$/;

/**
 * Confirmation dans le pied : question, détail, champ éventuel (référence) et corps envoyé. null : geste en un clic
 * (étape de la checklist). Montant du versement : celui du service (`money.outstandingInput`), jamais recalculé.
 */
export function confirmOf(sale, action) {
  const ref = sale.listing?.reference ?? '';
  const tokens = sale.tokens ?? {};
  switch (action) {
    case 'access': case 'email': return null;
    case 'conclude': {
      const missing = Array.isArray(tokens.missing) ? tokens.missing.filter((code) => STEP_WORDS[code]) : [];
      const unread = tokens.missing === null || tokens.missing === undefined;
      return { question: `Conclure la vente ${ref} ?`, detail: `Le bot poste « Sale complete » à l’acheteur et son ticket passe en lecture seule.${missing.length
        ? ` Non coché : ${missing.map((code) => STEP_WORDS[code]).join(', ')} ; la conclusion vaut accès remis et e-mail changé.` : unread ? ' La checklist n’a pas pu être relue ; la conclusion vaut accès remis et e-mail changé.' : ''}`,
      confirm: 'Conclure', body: { ...(tokens.version ? { version: tokens.version } : {}), ...(missing.length || unread ? { confirmIncomplete: true } : {}) } };
    }
    case 'mark_due': return { question: `Constater le règlement dû pour ${ref} ?`, detail: 'Après la livraison validée, le net du vendeur devient dû dans le registre. Rien n’est versé.',
      field: { label: 'Référence de la preuve', value: sale.id, hint: 'Le dossier de la vente, ou une autre référence interne vérifiable.' }, confirm: 'Constater',
      body: { sequence: sale.money?.sequence } };
    case 'payout': {
      const amount = Number.isSafeInteger(sale.money?.outstandingMinor) ? money(sale.money.outstandingMinor, sale.money.currency) : '';
      return { question: `Noter un versement de ${amount} au vendeur de ${ref} ?`, detail: 'Le versement est déjà fait (PayPal, virement) : il est seulement enregistré, rien n’est déclenché.',
        field: { label: 'Référence du versement', value: '', hint: 'Identifiant de la transaction, sans donnée bancaire.' }, confirm: 'Noter le versement',
        body: { sequence: sale.money?.sequence, amount: sale.money?.outstandingInput } };
    }
    case 'close': return { question: `Fermer les tickets de ${ref} ?`, detail: `${sale.gesture === 'settle' ? 'Le paiement du vendeur n’est pas suivi ici : fermez seulement s’il est payé. ' : ''}L’acheteur et le vendeur relisent, n’écrivent plus. /rouvrir dans un ticket pour revenir en arrière.`,
      confirm: 'Fermer les tickets', body: {} };
    default: return null;
  }
}

const CLOSE_WORDS = Object.freeze({ not_controlled: 'ticket MEE6, à fermer avec ses boutons', controls_disabled: 'à fermer avec /fermer', partial_failure: 'Discord n’a pas répondu',
  ticket_busy: 'une autre action est en cours', stage_full: 'Closed Tickets est plein' });
/** Toast après un geste réussi : { text, tone }. */
export function toastOf(sale, outcome, { exited = false } = {}) {
  const ref = sale.listing?.reference ?? '';
  if (outcome.noop && !outcome.replayed) return { text: `${ref} : déjà fait, rien de plus.`, tone: 'neutral' };
  switch (outcome.gesture) {
    case 'access': return { text: `${ref} : accès remis noté.`, tone: 'success' };
    case 'email': return { text: `${ref} : e-mail changé noté.`, tone: 'success' };
    case 'conclude': return { text: `${ref} : vente conclue, « Sale complete » posté à l’acheteur.`, tone: 'success' };
    case 'mark_due': return { text: `${ref} : règlement dû constaté.`, tone: 'success' };
    case 'payout': return { text: `${ref} : versement noté${outcome.audit === 'pending' ? ', reçu d’historique à vérifier' : ''}.`, tone: 'success' };
    case 'close': {
      if (outcome.partial) {
        const failed = Object.entries(outcome.tickets ?? {}).find(([, result]) => result.kind !== 'done');
        return { text: `${ref} : ticket ${failed?.[0] === 'seller' ? 'acheteur' : 'vendeur'} fermé ; ticket ${failed?.[0] === 'seller' ? 'vendeur' : 'acheteur'} non fermé (${CLOSE_WORDS[failed?.[1].code] ?? 'refusé'}).`, tone: 'warning' };
      }
      return { text: `${ref} : tickets fermés${exited ? ', la vente sort de la file' : ''}. /rouvrir pour revenir en arrière.`, tone: 'success' };
    }
    default: return { text: `${ref} : fait.`, tone: 'success' };
  }
}

const ERROR_TEXT = Object.freeze({
  forbidden: 'Ce geste est réservé aux administrateurs.',
  busy: 'Une autre action est en cours sur cette annonce. Réessayez dans un instant.',
  version_changed: 'La vente a changé entre-temps : elle vient d’être relue. Vérifiez avant de refaire le geste.',
  delivery_locked: 'La vente est déjà conclue : la checklist est figée.',
  delivery_unavailable: 'Le journal de livraison ne répond pas. Réessayez dans un instant.',
  discord: 'Discord ne répond pas : rien n’est confirmé. Vérifiez le ticket avant de réessayer.',
  controls_disabled: 'La fermeture depuis le site est coupée : utilisez /fermer dans chaque ticket.',
  not_controlled: 'Ticket MEE6 : fermez-le dans Discord avec ses boutons.',
  close_paid_undelivered: 'Vente payée non conclue : le ticket acheteur ne se ferme pas avant la conclusion.',
  seller_conversation_required: 'Le dossier du vendeur n’a pas de conversation rattachée : passez par l’espace équipe.',
  invalid_reference: 'Référence invalide : lettres, chiffres et - _ . : / seulement, sans espace.',
  duplicate_settlement_evidence: 'Cette référence de versement est déjà enregistrée.',
  seller_amount_not_due: 'Le règlement du vendeur n’est pas encore dû.',
  request_id_conflict: 'Ce geste a déjà été envoyé avec d’autres valeurs. Rechargez la page.',
  gesture_unavailable: 'Ce geste n’est plus proposé pour cette vente : elle vient d’être relue.',
  too_many: 'Trop de gestes d’affilée. Patientez quelques secondes.',
  network: 'Le service ne répond pas : rien n’est confirmé. Réessayez dans un instant.',
  other: 'Le geste a été refusé. La vente vient d’être relue.'
});
const SAME = Object.freeze({ ticket_busy: 'busy', control_pending: 'busy', changed_meanwhile: 'version_changed', sequence_conflict: 'version_changed', payout_amount_changed: 'version_changed',
  payout_exceeds_due: 'version_changed', delivery_incomplete: 'version_changed', partial_failure: 'discord', tickets_unavailable: 'discord', actions_not_ready: 'discord',
  seller_conversation_not_found: 'seller_conversation_required', seller_listing_required: 'seller_conversation_required', invalid_evidence: 'invalid_reference' });
/** Erreur d'un geste, dite en clair : { text, reload } (reload : relire la file, l'état a pu changer). */
export function errorOf(status, body) {
  const code = typeof body?.error === 'string' ? body.error : '';
  const key = status === 0 ? 'network' : status === 429 ? 'too_many' : status === 403 ? 'forbidden' : ERROR_TEXT[code] ? code : SAME[code] ?? (status >= 500 ? 'discord' : 'other');
  return { text: ERROR_TEXT[key], reload: !['forbidden', 'too_many', 'invalid_reference', 'network', 'discord'].includes(key) };
}

/** Montant du pied, sur la ligne du prix de la carte (publication seulement). */
export function figureOf(sale) {
  const m = sale.money;
  if (!m) return null;
  if (m.status === 'settled' && Number.isSafeInteger(m.paidMinor)) return { value: money(m.paidMinor, m.currency), label: 'net vendeur versé' };
  if (Number.isSafeInteger(m.netMinor)) return { value: money(m.netMinor, m.currency), label: 'net vendeur' };
  return null;
}

/** Ligne du fil : « RG042 · vente conclue par Zarokk ». */
export function feedLine(item) {
  const what = item.code === 'delivery_step' ? DELIVERY_STEPS[item.step] ?? 'livraison cochée' : FEED_TEXT[item.code] ?? '';
  return `${what}${item.by ? ` par ${text(item.by, 64)}` : ''}`;
}

/** Résumé sous le titre : « 2 gestes à faire, 1 vente attend quelqu'un. » */
export function summaryText(view) {
  const mine = view?.counts?.mine ?? 0, waiting = view?.counts?.waiting ?? 0;
  if (!mine && !waiting) return 'Aucune vente en cours.';
  const parts = [mine ? `${mine} ${mine > 1 ? 'gestes' : 'geste'} à faire` : 'Rien à faire', waiting ? `${waiting} ${waiting > 1 ? 'ventes attendent' : 'vente attend'} quelqu’un` : ''];
  return `${parts.filter(Boolean).join(', ')}.`;
}

/* ------------------------------------------------------------------ rendu */

const SVG = 'http://www.w3.org/2000/svg';
function discordGlyph() {
  const svg = document.createElementNS(SVG, 'svg'), shape = document.createElementNS(SVG, 'path');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false'); svg.setAttribute('class', 'fs-ts-glyph');
  shape.setAttribute('fill', 'currentColor');
  shape.setAttribute('d', 'M19.6 5.3A17 17 0 0 0 15.4 4l-.5 1a15.7 15.7 0 0 0-5.8 0l-.5-1a17 17 0 0 0-4.2 1.3C1.7 9.3 1 13.2 1.3 17a17 17 0 0 0 5.2 2.6l1.1-1.8a11 11 0 0 1-1.7-.8l.4-.3a12.2 12.2 0 0 0 11.4 0l.4.3-1.7.8 1.1 1.8a17 17 0 0 0 5.2-2.6c.4-4.4-.7-8.3-3.1-11.7ZM8.5 14.7c-1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9 1 1.9 2.1-.8 2.1-1.9 2.1Zm7 0c-1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9 1 1.9 2.1-.8 2.1-1.9 2.1Z');
  svg.append(shape);
  return svg;
}

function track(sale, now) {
  return h('ol', { class: 'fs-ts-track', 'aria-label': `Avancement de la vente ${sale.listing.reference}` }, sale.steps.map((step, index) => {
    const current = index === sale.current;
    return h('li', { class: 'fs-ts-step', 'data-state': step.state, 'data-current': current ? 'true' : null, 'aria-current': current ? 'step' : null },
      h('span', { class: 'fs-ts-step-name', text: STEP_NAMES[step.code] ?? step.code }),
      h('span', { class: 'fs-visually-hidden', text: ` : ${current && step.state !== 'unknown' ? 'en cours' : STATE_WORDS[step.state] ?? ''}, ` }),
      h('span', { class: 'fs-ts-step-time', text: stepTime(step, { current, now }) }),
      // Auteur d'une étape notée (checklist, conclusion, versement) : sous l'heure, coupé s'il est long.
      step.state === 'done' && step.by ? h('span', { class: 'fs-ts-step-by', text: text(step.by, 64) }) : null);
  }));
}

function ticketTile(kind, ticket, sale, now) {
  const who = kind === 'buyer' ? 'acheteur' : 'vendeur';
  const stage = STAGE_NAMES[ticket?.stage] && ticket.location === 'open' ? STAGE_NAMES[ticket.stage] : '';
  const where = !ticket ? 'Aucun ticket connu pour cette annonce'
    : ticket.location === 'deleted' ? 'Salon supprimé'
      : ticket.location === 'closed' ? (ticket.closedAt ? `Fermé le ${clock(ticket.closedAt, now)}` : 'Fermé')
        : ticket.location === 'unknown' ? 'Emplacement non vérifié'
          : ticket.lastMessageAt ? `message ${ago(ticket.lastMessageAt, now)}` : 'ouvert';
  const url = DISCORD_CHANNEL.test(ticket?.url ?? '') ? ticket.url : null;
  return h('div', { class: 'fs-ts-ticket', 'data-location': ticket?.location ?? 'none' },
    h('span', { class: 'fs-ts-ticket-mark', 'aria-hidden': 'true', text: kind === 'buyer' ? 'A' : 'V' }),
    h('p', { class: 'fs-ts-ticket-h', text: `Ticket ${who}` }),
    h('p', { class: 'fs-ts-ticket-meta', text: stage ? `${stage} · ${where}` : where }),
    url ? h('a', { class: 'fs-btn fs-ts-discord', href: url, target: '_blank', rel: 'noopener noreferrer',
      'aria-label': `Ouvrir le ticket ${who} de ${sale.listing.reference} dans Discord (nouvel onglet)` }, discordGlyph(), h('span', { text: 'Discord' }), h('span', { 'aria-hidden': 'true', text: ' ↗' })) : null);
}

function identity(sale) {
  const game = GAME_NAMES[sale.listing.game] ?? '';
  return h('div', { class: 'fs-ts-ident' },
    h('p', { class: 'fs-ts-ident-meta' }, game, game ? h('span', { 'aria-hidden': 'true', text: ' · ' }) : null, h('span', { class: 'fs-ts-ident-ref', text: sale.listing.reference })),
    h('p', { class: 'fs-ts-ident-title', text: sale.listing.title || sale.listing.reference }),
    h('p', { class: 'fs-ts-ident-note', text: sale.listing.path ? 'Carte indisponible pour le moment.' : 'Annonce retirée du site : pas de carte.' }));
}

function saleNode(sale, now) {
  const who = whoText(sale, now), say = sentence(sale, now), gesture = gestureOf(sale), figure = figureOf(sale);
  const titleId = `fs-ts-${sale.id}`;
  return h('article', { class: 'fs-ts-sale', 'data-fs-ts-sale': sale.id, 'data-fs-universe': GAME_NAMES[sale.listing.game] ? sale.listing.game : 'default', 'aria-labelledby': titleId },
    h('div', { class: 'fs-ts-cardcol', 'data-fs-ts-card': sale.listing.handle }, identity(sale)),
    h('div', { class: 'fs-ts-case' },
      h('div', { class: 'fs-ts-z1' },
        h('p', { class: 'fs-ts-who', 'data-tone': who.tone, text: who.text }),
        h('h3', { class: 'fs-ts-sum', id: titleId }, h('span', { class: 'fs-visually-hidden', text: `${sale.listing.reference} : ` }), say.lead,
          say.rest ? h('span', { class: 'fs-ts-sum-rest', text: ` ${say.rest}` }) : null),
        track(sale, now)),
      h('div', { class: 'fs-ts-z2' }, h('div', { class: 'fs-ts-tickets' }, ticketTile('buyer', sale.tickets.buyer, sale, now), ticketTile('seller', sale.tickets.seller, sale, now))),
      h('div', { class: 'fs-ts-z3', 'data-fs-ts-foot': true }, footParts(sale, gesture, figure))));
}

/** Contrôle du pied : bouton d'un geste (data-fs-ts-action), ou lien (relance dans Discord ↗, espace équipe). */
function control(sale, item, primary) {
  if (!item) return null;
  const tone = primary ? 'fs-btn fs-btn--primary' : 'fs-btn';
  if (typeof item === 'string') return h('button', { type: 'button', class: `${tone} fs-ts-do`, 'data-fs-ts-action': item, text: ACTION_LABELS[item] ?? item });
  if (item.external) {
    return h('a', { class: `${tone} fs-ts-do fs-ts-discord`, href: item.href, target: '_blank', rel: 'noopener noreferrer', 'aria-label': item.aria },
      discordGlyph(), h('span', { text: item.label }), h('span', { 'aria-hidden': 'true', text: ' ↗' }));
  }
  // Outil existant de l'équipe (dossier vendeur de l'espace équipe) : vrai lien de 44 px, même onglet.
  return h('a', { class: `${tone} fs-ts-do`, href: path(item.href), text: item.label });
}

/** Pied ordinaire : geste attendu, ses contrôles (principal puis secondaire, frères sur une ligne), montant. */
function footParts(sale, gesture = gestureOf(sale), figure = figureOf(sale)) {
  const controls = [control(sale, gesture.primary, true), control(sale, gesture.secondary, false)].filter(Boolean);
  return [
    h('div', { class: 'fs-ts-act' },
      h('p', { class: 'fs-ts-act-text', text: gesture.text }),
      gesture.hint || gesture.code ? h('p', { class: 'fs-ts-hint' }, gesture.hint ?? '', gesture.code ? ' ' : '', gesture.code ? h('code', { text: gesture.code }) : null) : null),
    controls.length ? h('div', { class: 'fs-ts-controls' }, controls) : null,
    figure ? h('p', { class: 'fs-ts-fig' }, h('span', { class: 'fs-price fs-price--card', text: figure.value }), h('span', { class: 'fs-ts-fig-label', text: figure.label })) : null];
}

function group(id, title, note, sales, now, empty) {
  const heading = `fs-ts-group-${id}`;
  return h('section', { class: 'fs-ts-group', 'data-fs-ts-group': id, 'aria-labelledby': heading },
    h('h2', { class: 'fs-ts-group-h', id: heading }, h('span', { text: title }), h('span', { class: 'fs-counter', text: String(sales.length) }), h('small', { class: 'fs-ts-group-note', text: note })),
    sales.length ? h('div', { class: 'fs-ts-list' }, sales.map((sale) => saleNode(sale, now))) : h('p', { class: 'fs-ts-calm', text: empty }));
}

/** Carte publique d'une annonce par la Section Rendering API ; null si la vitrine ne la montre pas. */
async function fetchCard(listingPath) {
  const response = await fetch(`${path(listingPath)}?section_id=${CARD_SECTION}`, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
  if (!response.ok) return null;
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const card = doc.querySelector('article.fs-lc');
  if (!card) return null;
  card.querySelectorAll('script').forEach((node) => node.remove());
  return document.importNode(card, true);
}

export function mountTeamSales(root) {
  const account = window.FSAccount;
  const stateBox = root.querySelector('[data-fs-ts-state]'), stateText = root.querySelector('[data-fs-ts-state-text]');
  const salesBox = root.querySelector('[data-fs-ts-sales]'), summary = root.querySelector('[data-fs-ts-summary]');
  const feed = root.querySelector('[data-fs-ts-feed]'), feedList = root.querySelector('[data-fs-ts-feed-list]');
  if (!account || !stateBox || !salesBox) return;
  const cards = new Map(); // handle → Promise<carte | null>, une demande par annonce et par page
  const wide = window.matchMedia('(min-width: 1080px)');
  let loadedAt = 0, loading = false, again = false, generation = 0, view = null;
  // Gestes : un à la fois par vente ; requestId gardé par intention tant que la réponse est incertaine (réseau, 5xx), pour
  // qu'un nouvel essai soit un rejeu et jamais un second geste.
  const busy = new Set(), intents = new Map();
  const still = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.fsMotion === 'reduced';

  function waitWith(message, { spinner = true, node = null } = {}) {
    salesBox.hidden = true; feed.hidden = true; put(summary);
    stateBox.hidden = false;
    stateBox.querySelector('.fs-spinner')?.toggleAttribute('hidden', !spinner);
    stateText.textContent = message;
    stateBox.querySelectorAll('[data-fs-ts-extra]').forEach((extra) => extra.remove());
    if (node) add(stateBox, h('div', { 'data-fs-ts-extra': true }, node));
  }
  /** Zones du dossier aux hauteurs de la baie et du pied de la carte voisine (bureau seulement). */
  function align(article) {
    const body = article.querySelector('.fs-ts-case'), bay = article.querySelector('.fs-ts-cardcol .fs-bay'), foot = article.querySelector('.fs-ts-cardcol .fs-lc-foot');
    if (!body) return;
    if (!wide.matches || !bay || !foot) { body.style.removeProperty('--fs-ts-z1'); body.style.removeProperty('--fs-ts-z3'); return; }
    body.style.setProperty('--fs-ts-z1', `${bay.offsetHeight}px`);
    body.style.setProperty('--fs-ts-z3', `${foot.offsetHeight}px`);
  }
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver((entries) => entries.forEach((entry) => { const article = entry.target.closest('.fs-ts-sale'); if (article) align(article); })) : null;
  wide.addEventListener?.('change', () => salesBox.querySelectorAll('.fs-ts-sale').forEach(align));

  async function placeCard(article, sale) {
    if (!sale.listing.path) return;
    if (!cards.has(sale.listing.handle)) cards.set(sale.listing.handle, fetchCard(sale.listing.path).catch(() => null));
    const card = await cards.get(sale.listing.handle);
    const slot = article.querySelector('[data-fs-ts-card]');
    // Vitrine sans carte pour cette annonce (retirée, brouillon) : l'identité lue sur le service reste, sans fausse carte.
    if (!card || !slot || !article.isConnected) return;
    put(slot, card.cloneNode(true));
    article.classList.add('has-card');
    align(article);
    const bay = slot.querySelector('.fs-bay'), foot = slot.querySelector('.fs-lc-foot');
    if (observer && bay && foot) { observer.observe(bay); observer.observe(foot); }
  }

  function render(next) {
    view = next;
    const now = Date.now();
    const mine = view.sales.filter((sale) => sale.group === 'mine'), waiting = view.sales.filter((sale) => sale.group === 'waiting');
    const notes = [];
    if (view.complete === false) notes.push(alert({ tone: 'warning', role: null, text: view.tickets === 'records'
      ? 'Discord n’a pas pu être relu : l’emplacement des tickets est celui enregistré par le site, il peut dater.'
      : 'Une partie des informations n’a pas pu être relue : certains états peuvent dater.', attrs: { class: 'fs-ts-note' } }));
    put(salesBox, notes, group('mine', 'À toi', 'du plus ancien au plus récent', mine, now, 'Rien à faire pour l’instant.'),
      group('waiting', 'On attend quelqu’un', 'rien à faire, sauf relancer dans Discord', waiting, now, 'Personne n’attend.'));
    observer?.disconnect();
    salesBox.querySelectorAll('.fs-ts-sale').forEach((article) => {
      const sale = view.sales.find((row) => row.id === article.dataset.fsTsSale);
      if (sale) placeCard(article, sale);
    });
    refreshChrome();
    feed.hidden = false;
    stateBox.hidden = true;
    salesBox.hidden = false;
  }

  /** Focus sur un titre (tabindex -1 posé au besoin), sans contour au clic. */
  function focusQuiet(node) {
    if (!node) return;
    if (!node.matches('a, button, input, [tabindex]')) node.setAttribute('tabindex', '-1');
    node.focus();
  }
  /** Pied ordinaire remis (après Annuler ou un refus) ; le focus revient au contrôle du geste. */
  function restoreFoot(article, sale, action = null) {
    const foot = article.querySelector('[data-fs-ts-foot]');
    if (!foot) return;
    delete article.dataset.confirming;
    put(foot, footParts(sale));
    align(article);
    if (action) (foot.querySelector(`[data-fs-ts-action="${action}"]`) ?? foot.querySelector('.fs-ts-do'))?.focus();
  }

  /** Confirmation dans le pied : question, détail, champ éventuel, « Confirmer » et « Annuler » (Échap). */
  function openConfirm(article, sale, action, ask) {
    const foot = article.querySelector('[data-fs-ts-foot]');
    if (!foot) return;
    const question = `fs-ts-q-${sale.id}`;
    const input = ask.field ? h('input', { type: 'text', value: ask.field.value, maxlength: '120', autocomplete: 'off', spellcheck: 'false', autocapitalize: 'off' }) : null;
    const box = input ? field(ask.field.label, input, { hint: ask.field.hint, errorSlot: true, className: 'fs-ts-confirm-field' }) : null;
    const submit = h('button', { type: 'submit', class: 'fs-btn fs-btn--primary', text: ask.confirm });
    const cancel = h('button', { type: 'button', class: 'fs-btn', text: 'Annuler', onclick: () => restoreFoot(article, sale, action) });
    const form = h('form', { class: 'fs-ts-confirm', 'aria-labelledby': question, novalidate: true },
      h('div', { class: 'fs-ts-confirm-text' }, h('p', { class: 'fs-ts-act-text', id: question, text: ask.question }), h('p', { class: 'fs-ts-hint', text: ask.detail })),
      box, h('div', { class: 'fs-ts-controls' }, submit, cancel));
    form.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !busy.has(sale.id)) { event.preventDefault(); restoreFoot(article, sale, action); } });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const body = { ...ask.body };
      if (input) {
        const value = input.value.trim(), error = box.querySelector('[data-error]');
        if (!setFieldError({ control: input, error, field: box, message: EVIDENCE.test(value) ? '' : 'Lettres, chiffres et - _ . : / seulement, sans espace (120 au plus).' })) body.evidence = value;
        else { input.focus(); return; }
      }
      perform(article, sale, action, body, [submit, cancel, input]);
    });
    article.dataset.confirming = 'true';
    put(foot, form);
    (input ?? submit).focus();
  }

  /** Envoi d'un geste ; la réponse porte la vente relue (ou sa sortie de la file), les compteurs et le fil. */
  async function perform(article, sale, action, body, controls = []) {
    if (busy.has(sale.id)) return;
    busy.add(sale.id);
    const pressed = controls.filter(Boolean);
    const origin = article.querySelector(`[data-fs-ts-action="${action}"]`);
    pressed.push(...(origin ? [origin] : []));
    pressed.forEach((node) => { node.disabled = true; });
    article.setAttribute('aria-busy', 'true');
    const key = `${sale.id}:${action}:${JSON.stringify(body)}`;
    if (!intents.has(key)) intents.set(key, crypto.randomUUID());
    let result;
    try { result = await account.call(`/admin/sales/${encodeURIComponent(sale.id)}/${action}`, { method: 'POST', body: { ...body, requestId: intents.get(key) } }); }
    catch { result = { status: 0, body: null }; }
    busy.delete(sale.id);
    article.removeAttribute('aria-busy');
    pressed.forEach((node) => { if (node.isConnected) node.disabled = false; });
    // Réponse certaine (succès ou refus) : l'intention est close ; incertaine (réseau, 5xx) : même requestId au prochain essai.
    if (result.status && result.status < 500) intents.delete(key);
    if (result.status === 200 && result.body?.outcome) { settle(article, sale, result.body); return; }
    const problem = errorOf(result.status, result.body);
    toast(problem.text, { tone: 'danger', dismissible: true, timeout: 9000 });
    if (problem.reload) load();
    else if (article.dataset.confirming) {
      // Le pied de confirmation reste (saisie gardée) : l'erreur est dite, le focus revient sur « Confirmer ».
      article.querySelector('.fs-ts-confirm [type="submit"]')?.focus();
    } else restoreFoot(article, sale, action);
  }

  /** Après un geste réussi : vente relue en place (même carte), ou sortie de la file ; compteurs, fil, toast. */
  function settle(article, sale, answer) {
    const next = answer.sale, notice = toastOf(sale, answer.outcome, { exited: answer.exited });
    view = { ...view, counts: answer.counts ?? view.counts, feed: Array.isArray(answer.feed) ? answer.feed : view.feed,
      sales: next ? view.sales.map((row) => (row.id === sale.id ? next : row)) : view.sales.filter((row) => row.id !== sale.id) };
    toast(notice.text, { tone: notice.tone, timeout: notice.tone === 'warning' ? 9000 : 6000, dismissible: notice.tone === 'warning' });
    if (!next) { leave(article); return; }
    if (next.group !== sale.group) {
      // Changement de groupe (« À toi » ↔ « On attend ») : la file est reposée, le focus suit la vente.
      render(view);
      focusQuiet(salesBox.querySelector(`[data-fs-ts-sale="${CSS.escape(next.id)}"] .fs-ts-sum`));
      return;
    }
    const fresh = saleNode(next, Date.now()), card = article.querySelector('.fs-ts-cardcol');
    fresh.querySelector('.fs-ts-cardcol').replaceWith(card);
    if (article.classList.contains('has-card')) fresh.classList.add('has-card');
    article.replaceWith(fresh);
    align(fresh);
    refreshChrome();
    const focus = fresh.querySelector('.fs-ts-do') ?? fresh.querySelector('.fs-ts-sum');
    focus?.focus();
  }

  /** Compteurs des groupes, résumé et fil, sans reposer les ventes. */
  function refreshChrome() {
    put(summary, summaryText(view));
    for (const id of ['mine', 'waiting']) {
      const counter = salesBox.querySelector(`[data-fs-ts-group="${id}"] .fs-counter`);
      if (counter) counter.textContent = String(view.sales.filter((row) => row.group === id).length);
    }
    const now = Date.now();
    put(feedList, view.feed.length ? view.feed.map((item) => h('li', { class: 'fs-ts-feed-item' }, h('time', { datetime: item.at, text: clock(item.at, now) }),
      h('span', {}, h('b', { text: text(item.reference, 40) }), ` · ${feedLine(item)}`))) : h('li', { class: 'fs-ts-feed-item fs-ts-feed-none', text: 'Rien depuis 7 jours.' }));
  }

  /**
   * Sortie de la file : le tampon « Vendu » de la carte est refrappé (fs-sold.css), puis le dossier se replie ; en
   * mouvement réduit, il part sans animation. Le focus passe à la vente suivante, sinon au titre du groupe.
   */
  function leave(article) {
    const group = article.closest('[data-fs-ts-group]');
    const next = article.nextElementSibling ?? article.previousElementSibling;
    const done = () => {
      article.remove();
      const list = group?.querySelector('.fs-ts-list');
      if (list && !list.children.length) list.replaceWith(h('p', { class: 'fs-ts-calm', text: group.dataset.fsTsGroup === 'mine' ? 'Rien à faire pour l’instant.' : 'Personne n’attend.' }));
      refreshChrome();
      focusQuiet(next?.isConnected ? next.querySelector('.fs-ts-sum') : group?.querySelector('.fs-ts-group-h'));
    };
    if (still() || typeof article.animate !== 'function') { done(); return; }
    article.dataset.leaving = 'true';
    const stamp = article.querySelector('[data-fs-sold-stamp]');
    if (stamp) { stamp.classList.remove('is-stamped'); stamp.classList.add('is-armed'); void stamp.offsetWidth; stamp.classList.remove('is-armed'); stamp.classList.add('is-stamped'); }
    // Repli : hauteur et écart de la liste rendus ensemble (l'écart du côté d'un voisin), sans saut à la fin.
    const height = article.offsetHeight, gap = getComputedStyle(article.parentElement).rowGap || '0px';
    const side = article.nextElementSibling ? 'marginBottom' : article.previousElementSibling ? 'marginTop' : null;
    const fold = () => article.animate([{ height: `${height}px`, opacity: 1, ...(side ? { [side]: '0px' } : {}) }, { height: '0px', opacity: 0, ...(side ? { [side]: `calc(-1 * ${gap})` } : {}) }],
      { duration: 380, easing: 'cubic-bezier(.4, 0, .2, 1)', fill: 'forwards' }).finished.then(done, done);
    setTimeout(fold, stamp ? 700 : 120);
  }

  salesBox.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-fs-ts-action]');
    if (!button || !salesBox.contains(button) || button.disabled) return;
    const article = button.closest('[data-fs-ts-sale]');
    const sale = view?.sales.find((row) => row.id === article?.dataset.fsTsSale);
    if (!sale || busy.has(sale.id) || !sale.actions?.includes(button.dataset.fsTsAction)) return;
    const ask = confirmOf(sale, button.dataset.fsTsAction);
    if (ask) openConfirm(article, sale, button.dataset.fsTsAction, ask);
    else perform(article, sale, button.dataset.fsTsAction, { version: sale.tokens?.delivery ?? undefined });
  });

  async function load() {
    // Une demande arrivée pendant une lecture (changement de session) n'est pas perdue : elle relance une lecture à la fin.
    if (loading) { again = true; return; }
    loading = true; again = false;
    const mine = ++generation;
    try {
      if (!account.session()) {
        waitWith('Connectez-vous avec Discord pour voir les ventes en cours.', { spinner: false,
          node: h('button', { type: 'button', class: 'fs-btn fs-btn--primary', onclick: () => account.login({ kind: 'account', returnTo: 'ventes' }) }, 'Se connecter avec Discord') });
        return;
      }
      const rights = await account.me();
      if (mine !== generation) return;
      if (!rights) { waitWith('Le service ou Discord ne répond pas : vos droits n’ont pas pu être vérifiés. Réessayez dans un instant.', { spinner: false }); return; }
      if (!rights.team) { waitWith('Cette page est réservée à l’équipe Space Syndicate.', { spinner: false, node: h('a', { class: 'fs-btn', href: path('/') }, 'Retour aux annonces') }); return; }
      if (!loadedAt) waitWith('Lecture des ventes en cours…');
      const { status, body } = await account.call('/admin/sales');
      if (mine !== generation) return;
      if (status === 403) { waitWith('Cette page est réservée à l’équipe Space Syndicate.', { spinner: false }); return; }
      if (status !== 200 || !Array.isArray(body?.sales)) { if (!loadedAt) waitWith('Les ventes en cours n’ont pas pu être lues. Réessayez dans un instant.', { spinner: false }); return; }
      loadedAt = Date.now();
      render(body);
    } catch {
      if (!loadedAt) waitWith('Le service ne répond pas. Réessayez dans un instant.', { spinner: false });
    } finally {
      loading = false;
      if (again) load();
    }
  }

  document.addEventListener('fs:account', () => { loadedAt = 0; load(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && Date.now() - loadedAt >= REFRESH_MS) load(); });
  load();
}

if (typeof document !== 'undefined') {
  const start = () => document.querySelectorAll('[data-fs-team-sales]').forEach((root) => {
    if (root.dataset.fsTsReady) return;
    const go = () => { root.dataset.fsTsReady = 'true'; mountTeamSales(root); };
    if (window.FSAccount) go(); else { let tries = 0; const wait = () => (window.FSAccount ? go() : tries++ < 100 ? setTimeout(wait, 50) : null); wait(); }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
}
